/**
 * Date de naissance + choix du scrutin + verdict d'éligibilité (18 ans).
 *
 * UN seul composant pour les DEUX portes d'entrée d'un bénéficiaire :
 *   - le Relais qui enrôle      (pages/relais/CreateBeneficiaire.jsx)
 *   - le citoyen qui sollicite  (demande d'accompagnement)
 * Les deux posent exactement la même question ; deux copies divergeraient.
 *
 * Spec : documentation/ELIGIBILITY_ELECTION_SELECTION_SPEC.md §6.
 * L'app ne choisit JAMAIS le scrutin (D3/D5) : sélecteur seulement à partir de 2
 * scrutins (D4), aucune présélection, verdict après un choix conscient.
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import * as electionsService from '../../services/elections.service';
import './ElectionEligibilityField.css';

/** Debounce de la vérification serveur, aligné sur l'existant. */
const CHECK_DEBOUNCE_MS = 400;

/** Une élection est exploitable si elle porte une date de référence (D2). */
function referenceDateOf(election) {
  return election?.date_election || election?.inscription_date_fin || null;
}

/**
 * Une date d'élection est une date CIVILE (colonne DATE, sans heure).
 * `new Date('2029-02-25')` l'interprète en UTC, puis `toLocaleDateString` rend en heure
 * LOCALE : un utilisateur à l'ouest de Greenwich (diaspora américaine) lisait
 * « 24 février » pour un scrutin du 25. On reconstruit donc une date locale à partir
 * des composantes Y-M-D, sans jamais traverser de fuseau.
 */
function formatDate(value) {
  if (!value) return '';
  const [y, m, d] = String(value).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return String(value);
  return new Date(y, m - 1, d)
    .toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}

/**
 * Libellé canonique d'un type de scrutin (miroir de backend/src/utils/eligibility.util.js).
 * Le type est saisi par l'admin ; des données anciennes portent un type brut
 * (« legislatives ») : on le normalise, et un type inconnu reste tel quel (F-14).
 */
const ELECTION_TYPE_LABELS = {
  legislatives: 'Législatives',
  municipales: 'Municipales',
  presidentielle: 'Présidentielle',
};

function electionTypeLabel(type) {
  const raw = String(type || '').trim();
  const key = raw.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  return ELECTION_TYPE_LABELS[key] || raw;
}

function electionLabel(election) {
  const ref = referenceDateOf(election);
  const type = electionTypeLabel(election.type);
  return ref ? `${type} du ${formatDate(ref)}` : type;
}

export default function ElectionEligibilityField({
  dateNaissance = '',
  onDateNaissanceChange,
  electionId = null,
  onElectionIdChange,
  onEligibilityChange = undefined,
  audience = 'relais',
  disabled = false,
  error = '',
}) {
  const [elections, setElections] = useState([]);
  const [loadingElections, setLoadingElections] = useState(true);
  const [eligibility, setEligibility] = useState(null);
  const [checking, setChecking] = useState(false);
  const [checkFailed, setCheckFailed] = useState(false);
  // F-21 : « Réessayer » relance la MÊME vérification (même date, même scrutin).
  // Avant, la seule issue était de modifier la date — chaque modification consommait
  // du quota de débit et éloignait la saisie de la bonne valeur.
  const [retryNonce, setRetryNonce] = useState(0);

  // Le résultat courant doit rester lisible par le parent sans re-render en boucle.
  const notifyRef = useRef(onEligibilityChange);
  notifyRef.current = onEligibilityChange;

  const usableElections = useMemo(
    () => elections.filter((e) => referenceDateOf(e)),
    [elections]
  );
  const needsPicker = usableElections.length > 1;   // D4
  const isCitizen = audience === 'citoyen';

  // Scrutin prérempli sorti de la campagne (F-19). La porte B préremplit
  // users.election_id, figé à l'inscription (S9). Une fois ce scrutin passé ou
  // désactivé, le vérifier ne peut que répondre ELECTION_NOT_IN_CAMPAIGN — et avec un
  // seul scrutin restant, aucun sélecteur ne permettait d'en sortir. On l'efface donc
  // (le serveur retient alors le scrutin unique, ou le sélecteur s'affiche vide — D5)
  // et on le DIT au citoyen : rien ne change dans son dos.
  const electionIsStale = Boolean(electionId) && !loadingElections
    && !usableElections.some((e) => String(e.id) === String(electionId));
  // Tant que la liste n'est pas chargée, on ne sait pas si l'identifiant reçu vaut
  // encore : ne rien demander au serveur avec lui.
  const electionPending = Boolean(electionId) && (loadingElections || electionIsStale);
  const [staleElectionCleared, setStaleElectionCleared] = useState(false);

  const onElectionIdChangeRef = useRef(onElectionIdChange);
  onElectionIdChangeRef.current = onElectionIdChange;

  useEffect(() => {
    if (!electionIsStale) return;
    setStaleElectionCleared(true);
    onElectionIdChangeRef.current?.(null);
  }, [electionIsStale]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await electionsService.getCampaignElections();
        if (!cancelled && response?.success) setElections(response.data || []);
      } catch {
        if (!cancelled) setElections([]);
      } finally {
        if (!cancelled) setLoadingElections(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    // INVALIDER D'ABORD. Un verdict ne vaut que pour le couple (date, scrutin) qui l'a
    // produit : dès que l'un des deux change, l'ancien doit disparaître AVANT la
    // temporisation. Sans cela, un « canVote: true » survivait ~400 ms + aller-retour
    // réseau à un changement de scrutin — le sélecteur affichait le nouveau scrutin, le
    // verdict vert nommait l'ancien, et le bouton d'envoi restait actif : on pouvait
    // soumettre pour un scrutin approuvé pour un autre.
    setEligibility(null);
    setCheckFailed(false);
    notifyRef.current?.(null);

    if (!dateNaissance) {
      setChecking(false);
      return undefined;
    }
    // Avec plusieurs scrutins, ne rien demander tant qu'aucun n'est choisi : le serveur
    // répondrait ELECTION_REQUIRED, message inutile avant que l'utilisateur ait agi.
    if (needsPicker && !electionId) {
      setChecking(false);
      return undefined;
    }
    // Identifiant pas encore confirmé par la liste de campagne, ou périmé (F-19) :
    // attendre le chargement, ou l'effacement qui relancera la vérification.
    if (electionPending) {
      setChecking(false);
      return undefined;
    }

    // Signalé dès maintenant, pas dans la temporisation : sinon la fenêtre de 400 ms
    // se présente comme « rien en cours » au lieu de « vérification en cours ».
    setChecking(true);

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const response = await electionsService.checkEligibility(dateNaissance, electionId || null);
        if (cancelled) return;
        if (response?.success) {
          setEligibility(response.data);
          notifyRef.current?.(response.data);
        } else {
          // Un 200 porteur de `{success:false}` ne doit pas laisser l'état précédent en
          // place : troisième cas non traité dans un composant dont tout le principe
          // est de se fermer par défaut.
          setCheckFailed(true);
          notifyRef.current?.(null);
        }
      } catch {
        // Échec réseau ≠ « pas encore vérifié » : sans cette distinction, l'ancien code
        // remettait l'état à null et RÉACTIVAIT l'envoi, l'utilisateur se prenant une
        // erreur brutale à la soumission.
        if (!cancelled) {
          setEligibility(null);
          setCheckFailed(true);
          notifyRef.current?.(null);
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    }, CHECK_DEBOUNCE_MS);

    return () => { cancelled = true; clearTimeout(timer); };
  }, [dateNaissance, electionId, needsPicker, electionPending, retryNonce]);

  const noElection = !loadingElections && usableElections.length === 0;
  const soleElection = usableElections.length === 1 ? usableElections[0] : null;
  const showTooYoung = eligibility && !eligibility.canVote
    && (eligibility.code === 'TOO_YOUNG_FOR_ELECTION' || eligibility.code === 'TOO_YOUNG_FOR_ALL');

  return (
    <div className="ge-eligibility">
      {noElection && (
        <div className="ge-eligibility-blocked" role="alert">
          Aucune élection n&apos;est configurée pour le moment. Contactez l&apos;administrateur.
        </div>
      )}

      <div className="form-group">
        <label htmlFor="date_naissance">
          {isCitizen ? 'Votre date de naissance' : 'Date de naissance'}{' '}
          <span className="required" aria-label="requis">*</span>
        </label>
        <input
          type="date"
          id="date_naissance"
          name="date_naissance"
          value={dateNaissance}
          onChange={(e) => onDateNaissanceChange(e.target.value)}
          required
          aria-required="true"
          aria-invalid={Boolean(error) || Boolean(eligibility && !eligibility.canVote)}
          aria-describedby="ge-eligibility-verdict"
          className={error || (eligibility && !eligibility.canVote) ? 'error' : ''}
          max={new Date().toISOString().split('T')[0]}
          disabled={disabled || noElection}
        />
        {error && <span className="field-error" role="alert">{error}</span>}
      </div>

      {needsPicker && (
        <div className="form-group">
          <label htmlFor="election_id">
            Pour quelle élection ? <span className="required" aria-label="requis">*</span>
          </label>
          <select
            id="election_id"
            name="election_id"
            value={electionId || ''}
            onChange={(e) => onElectionIdChange(e.target.value || null)}
            required
            aria-required="true"
            disabled={disabled}
          >
            {/* Aucune option présélectionnée (D5) : le choix doit être conscient. */}
            <option value="">Choisir une élection…</option>
            {usableElections.map((e) => (
              <option key={e.id} value={e.id}>{electionLabel(e)}</option>
            ))}
          </select>
          <small className="form-hint">
            {isCitizen
              ? 'L’élection pour laquelle vous souhaitez vous inscrire.'
              : 'L’élection pour laquelle vous enrôlez ce citoyen.'}
          </small>
        </div>
      )}

      {/* Scrutin prérempli sorti de la campagne (F-19) : on le dit, et s'il ne reste
          qu'un scrutin on le nomme dans la même phrase. */}
      {staleElectionCleared && !loadingElections && usableElections.length > 0
        && (soleElection || !electionId || electionIsStale) && (
        <p className="ge-eligibility-hint ge-eligibility-hint-warn" role="status">
          {isCitizen
            ? 'Le scrutin choisi à votre inscription n’est plus dans la campagne'
            : 'Le scrutin précédemment choisi n’est plus dans la campagne'}
          {soleElection ? (
            <> — élection en cours : <strong>{electionLabel(soleElection)}</strong>.</>
          ) : (
            '. Choisissez l’élection ci-dessus.'
          )}
        </p>
      )}

      {/* Scrutin unique : pas de menu, mais on le NOMME — rien n'est caché (D4). */}
      {soleElection && !staleElectionCleared && !eligibility && dateNaissance && !checking && (
        <p className="ge-eligibility-hint">
          Élection en cours : <strong>{electionLabel(soleElection)}</strong>
        </p>
      )}

      <div id="ge-eligibility-verdict" aria-live="polite">
        {checking && (
          <p className="ge-eligibility-hint">Vérification de l’éligibilité…</p>
        )}

        {!checking && checkFailed && (
          <div className="ge-eligibility-retry" role="alert">
            <p className="ge-eligibility-hint ge-eligibility-hint-warn">
              Impossible de vérifier l’éligibilité pour le moment. Vérifiez votre connexion
              puis réessayez.
            </p>
            {/* Secondaire, jamais l'or : l'or reste réservé à l'envoi du formulaire. */}
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setRetryNonce((n) => n + 1)}
              disabled={disabled}
            >
              Réessayer
            </button>
          </div>
        )}

        {!checking && needsPicker && !electionId && dateNaissance && (
          <p className="ge-eligibility-hint">
            Choisissez l’élection pour vérifier l’éligibilité.
          </p>
        )}

        {!checking && eligibility?.canVote && (
          // Le motif renvoyé par le serveur est écrit à la 3e personne (« Le bénéficiaire
          // aura … »), rédigé pour le Relais qui enrôle. Servi tel quel au citoyen, il lui
          // parlait de lui-même à la troisième personne. On reformule donc côté client à
          // partir des mêmes données (age + election) — comme le fait déjà la carte
          // « trop jeune » juste en dessous.
          <p className="ge-eligibility-ok">
            {isCitizen && eligibility.age != null && eligibility.election
              ? `Vous aurez ${eligibility.age} ans au scrutin : ${electionLabel(eligibility.election)} — vous pouvez vous inscrire.`
              : eligibility.reason}
          </p>
        )}

        {!checking && showTooYoung && (
          // Ambre (jamais l'or : réservé au prochain pas actionnable — DESIGN.md).
          // On NOMME le scrutin qui conviendrait, sans bouton de bascule (D8) :
          // c'est l'utilisateur qui change sa sélection.
          // F-14 : UNE source pour les deux publics — la copie est construite ici à partir
          // de age + election + workable. Le motif serveur (qui contient déjà les deux
          // phrases) n'est plus affiché : la ligne alternative le répétait.
          // Pas d'âge chiffré pour le scrutin qui conviendrait : il n'est pas transmis, et
          // « 18 ans » était faux dès que la personne en aurait davantage.
          <div className="ge-eligibility-too-young" role="alert">
            <p className="ge-eligibility-too-young-main">
              {isCitizen ? 'Vous n’aurez que' : 'Il ou elle n’aura que'}{' '}
              {`${eligibility.age} ans au scrutin : ${electionLabel(eligibility.election)}.`}
            </p>
            {eligibility.workable ? (
              <p className="ge-eligibility-too-young-alt">
                {isCitizen ? 'Vous aurez' : 'Il ou elle aura'} l’âge de voter au scrutin :{' '}
                <strong>{electionLabel(eligibility.workable)}</strong>.
              </p>
            ) : (
              <p className="ge-eligibility-too-young-alt">
                Aucune élection de la campagne ne permet encore de voter.
              </p>
            )}
          </div>
        )}

        {!checking && eligibility && !eligibility.canVote && !showTooYoung
          && eligibility.code !== 'ELECTION_REQUIRED' && (
          <p className="ge-eligibility-ko" role="alert">{eligibility.reason}</p>
        )}
      </div>
    </div>
  );
}

ElectionEligibilityField.propTypes = {
  dateNaissance: PropTypes.string,
  onDateNaissanceChange: PropTypes.func.isRequired,
  electionId: PropTypes.string,
  onElectionIdChange: PropTypes.func.isRequired,
  /** Reçoit le verdict complet (ou null) — le parent bloque l'envoi sur `canVote`. */
  onEligibilityChange: PropTypes.func,
  /** Adapte la copie : 'relais' (3e personne) ou 'citoyen' (1re personne). */
  audience: PropTypes.oneOf(['relais', 'citoyen']),
  disabled: PropTypes.bool,
  error: PropTypes.string,
};
